<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { API, HOST_TULUM } from '@/utils/http'
import { formatTime } from '@/utils/datetime'
import { toast } from '@/components/ui/toast'
import { Spinner } from '@/components/ui/spinner'
import { Badge } from '@/components/ui/badge'

const route = useRoute()
const store = useStore()

const props = defineProps({
  layer: {
    type: Object,
    required: true
  },
  activeTab: {
    type: String,
    required: true
  }
})

const orgId = computed(() => route.params.org_id)
const settings = computed(() => store.getters['user/getUserSettings'])
const itemData = ref(null)
const isLoading = ref(false)
const error = ref(null)

const item_id = computed(() => {
  return props.layer?.properties?.layer?.data?.item_ids?.[0] || null
})

const item_ids = computed(() => {
  return props.layer?.properties?.layer?.data?.item_ids || []
})

const item_type = computed(() => {
  return props.layer?.properties?.layer?.data?.item_type || 'PSScene'
})

const hasItemData = computed(() => {
  return item_ids.value.length > 0
})

const fetchItemData = async () => {
  if (!item_id.value || !orgId.value || !item_type.value) return

  isLoading.value = true
  error.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST_TULUM}/search/item/${orgId.value}/${item_type.value}/${item_id.value}`
    })

    itemData.value = response.data
  } catch (err) {
    error.value = err.response?.data?.detail || 'Failed to fetch item data'
  } finally {
    isLoading.value = false
  }
}

// Watch for changes and fetch data when component mounts or item_id changes
watch([item_id, orgId], () => {
  if (item_id.value && orgId.value) {
    fetchItemData()
  }
}, { immediate: true })

onMounted(() => {
  if (item_id.value && orgId.value) {
    fetchItemData()
  }
})
</script>

<template>
  <div>
    <!-- Data Tab Content -->
    <div v-if="activeTab === 'data'">
      <div v-if="hasItemData" class="space-y-4">
        <div v-if="isLoading" class="flex items-center justify-center py-8">
          <Spinner class="w-6 h-6 mr-2" />
          <span class="text-sm text-muted-foreground">Loading item data...</span>
        </div>

        <div v-else-if="error" class="p-4 border border-red-200 rounded-lg bg-red-50">
          <h4 class="font-medium text-red-800 mb-2">Error loading item data</h4>
          <p class="text-sm text-red-600">{{ error?.message }}</p>
        </div>

        <div v-else-if="itemData" class="space-y-3">
          <div class="space-y-2">
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium">Item Type</span>
              <Badge variant="outline">{{ itemData.properties?.item_type }}</Badge>
            </div>

            <div v-if="itemData.properties?.acquired" class="flex justify-between items-center">
              <span class="text-sm font-medium">Acquired</span>
              <span class="text-sm text-muted-foreground">{{ formatTime(itemData.properties.acquired, settings.tz, 'D MMMM YYYY HH:mm') }}</span>
            </div>

            <div v-if="itemData.properties?.published" class="flex justify-between items-center">
              <span class="text-sm font-medium">Published</span>
              <span class="text-sm text-muted-foreground">{{ formatTime(itemData.properties.published, settings.tz, 'D MMMM YYYY HH:mm') }}</span>
            </div>

            <div v-if="itemData.properties?.cloud_cover !== undefined" class="flex justify-between items-center">
              <span class="text-sm font-medium">Cloud Cover</span>
              <span class="text-sm text-muted-foreground">{{ itemData.properties.cloud_cover }}%</span>
            </div>

            <div v-if="itemData.properties?.clear_confidence_percent !== undefined" class="flex justify-between items-center">
              <span class="text-sm font-medium">Clear Confidence</span>
              <span class="text-sm text-muted-foreground">{{ itemData.properties.clear_confidence_percent }}%</span>
            </div>

            <div v-if="itemData.properties?.pixel_resolution !== undefined" class="flex justify-between items-center">
              <span class="text-sm font-medium">Pixel Resolution</span>
              <span class="text-sm text-muted-foreground">{{ itemData.properties.pixel_resolution }}m/px</span>
            </div>
          </div>

          <!-- Raw Data Preview -->
          <!-- <div class="mt-6 space-y-3">
            <h4 class="font-medium text-sm uppercase tracking-wide text-muted-foreground">Raw Data</h4>
            <div class="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre class="text-xs font-mono text-gray-700 whitespace-pre-wrap">{{ JSON.stringify(itemData, null, 2) }}</pre>
            </div>
          </div> -->
        </div>
      </div>
      <p v-else class="text-sm text-muted-foreground">No Planet Order item data available</p>
    </div>

    <!-- Filter Tab Content -->
    <div v-if="activeTab === 'filter'">
      <p class="text-sm text-muted-foreground">Planet Order filters will be available here</p>
    </div>
  </div>
</template>
